import { logAmplitudeEvent } from 'amplitude/amplitude'
import { FeatureFlagName } from 'util/featureFlags'
import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import {
  PerColumnRailBomGeneratorAbstract,
  VerticalRailPosition,
} from '../../bom_generators/PerColumnRailBomGeneratorAbstract'
import {
  HorizontalRailPosition,
  PerRowRailBomGeneratorAbstract,
} from '../../bom_generators/PerRowRailBomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../../MountingSystemAbstract'
import { CutAndSplicerAbstract } from '../../post_processors/CutAndSplicerAbstract'
import { RoofHookAdderAbstract } from '../../post_processors/RoofHookAdderAbstract'
import {
  BomGeneratorContext,
  CompatibilityParameters,
  Component,
  Direction,
  Fastener,
  Item,
  MountingCalcInput,
  MountingCalcResult,
  MountingComponentType,
  Panel,
  PanelBlock,
  RailComponent,
  RailType,
  RoofTypeName,
} from '../../types'
import { addItems } from '../../utils'

/** K2 Spax wood screw line item; DB SKU is a pack of 50 not individual screws. */
export const K2_UK_SPAX_WOOD_SCREW_PACK_CODE = '2004111'
export const K2_UK_SPAX_SCREWS_PER_PACK = 50

/** SingleRail + tile wood screw; DB SKU is a pack of 100 (not individual screws). */
export const K2_UK_SINGLE_RAIL_TILE_WOOD_SCREW_PACK_CODE = '2004106'
export const K2_UK_SINGLE_RAIL_TILE_SCREWS_PER_PACK = 100

export function collapseK2UKSingleRailTileWoodScrewPackItems(items: Item[]): Item[] {
  const screwLineItems = items.filter((item) => item.name === K2_UK_SINGLE_RAIL_TILE_WOOD_SCREW_PACK_CODE)
  if (screwLineItems.length === 0) return items
  const packCount = Math.ceil(screwLineItems.length / K2_UK_SINGLE_RAIL_TILE_SCREWS_PER_PACK)
  const withoutScrews = items.filter((item) => item.name !== K2_UK_SINGLE_RAIL_TILE_WOOD_SCREW_PACK_CODE)
  const packLineItems: Item[] = Array.from({ length: packCount }, () => ({
    name: K2_UK_SINGLE_RAIL_TILE_WOOD_SCREW_PACK_CODE,
    components: [],
  }))
  return [...withoutScrews, ...packLineItems]
}

export function collapseK2UKSpaxWoodScrewPackItems(items: Item[]): Item[] {
  const screwLineItems = items.filter((item) => item.name === K2_UK_SPAX_WOOD_SCREW_PACK_CODE)
  if (screwLineItems.length === 0) return items
  const packCount = Math.ceil(screwLineItems.length / K2_UK_SPAX_SCREWS_PER_PACK)
  const withoutScrews = items.filter((item) => item.name !== K2_UK_SPAX_WOOD_SCREW_PACK_CODE)
  const packLineItems: Item[] = Array.from({ length: packCount }, () => ({
    name: K2_UK_SPAX_WOOD_SCREW_PACK_CODE,
    components: [],
  }))
  return [...withoutScrews, ...packLineItems]
}

export const roofTypesCompatibleWithK2UK: RoofTypeName[] = [
  'Composition / Asphalt Shingle',
  'Tar and Gravel / Bitumen',
  'Tile Clay',
  'Tile Concrete',
  'Tile Slate',
  'Trapezoidal',
  'Metal Tin',
]

export class K2OnRoofUK extends MountingSystemAbstract {
  layoutAdjusterClass = K2LayoutAdjuster
  bomGeneratorClass = K2OnRoofUKBomGenerator

  getFastener(): Fastener {
    return {
      name: K2_UK_SPAX_WOOD_SCREW_PACK_CODE,
      includedInRoofHookProduct: false,
      qtyPerRoofHook: 2,
      length: 100, // mm
      diameter: 8, // mm
      components: [],
    }
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypeRequired: true,
      roofTypes: roofTypesCompatibleWithK2UK,
      slopeRange: [5, 75], // Roof pitch: 5-75 degrees
      featureFlag: 'mounting_k2uk' as FeatureFlagName,
    }
  }
}

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: 12, verticalSpacing: 12 },
}

class K2LayoutAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

const railOverhang = 50 // mm - rail overhang value for K2 UK

class K2UKRoofHookAdder extends RoofHookAdderAbstract {
  spacingRuleSet = spacingRuleSet
  railOverhang = railOverhang

  getRailType(input: MountingCalcInput): RailType {
    const railType = input.options.k2UKRailType as
      | 'minirail'
      | 'singleRail'
      | 'solidRail'
      | 'multiRail10'
      | 'multiRail25'
      | 'multiRail10CSM'
      | 'multiRail25CSM'
      | undefined
    return railType === 'minirail' ? 'mini' : 'continuous'
  }

  chooseHook(input: MountingCalcInput, panelBlock: PanelBlock, rail: RailComponent): Item[] {
    const roofTypeName = input.roof.roofTypeName
    const railType = input.options.k2UKRailType as
      | 'minirail'
      | 'singleRail'
      | 'solidRail'
      | 'multiRail10'
      | 'multiRail25'
      | 'multiRail10CSM'
      | 'multiRail25CSM'
      | undefined
    let componentCode = '2003215' // Default fallback

    if (roofTypeName === 'Tile Slate') {
      if (railType === 'solidRail') {
        componentCode = '1000214'
      }
    } else if (roofTypeName === 'Tile Clay') {
      if (railType === 'solidRail') {
        componentCode = '2004244'
      } else if (railType === 'singleRail') {
        //TODO: Add 2004416 on component database
        componentCode = '2004416'
      }
    } else if (roofTypeName === 'Tile Concrete') {
      if (railType === 'solidRail') {
        componentCode = '2004244'
      } else if (railType === 'singleRail') {
        componentCode = '2003175'
      }
    }

    return [
      {
        name: componentCode,
        components: [
          {
            type: MountingComponentType.ROOF_HOOK,
            name: componentCode,
            top: 0,
            left: 0,
          },
        ],
      },
    ]
  }

  chooseFastener(hook: Item[], input?: MountingCalcInput): Fastener {
    const mountingSystem = new K2OnRoofUK(input!)
    const baseFastener = mountingSystem.getFastener()
    const railType = input?.options?.k2UKRailType as
      | 'minirail'
      | 'singleRail'
      | 'solidRail'
      | 'multiRail10'
      | 'multiRail25'
      | 'multiRail10CSM'
      | 'multiRail25CSM'
      | undefined
    const roofTypeName = input?.roof?.roofTypeName

    if (railType === 'singleRail') {
      // SingleRail uses 2004106 (pack of 100) for wood screws if roof type is any tile
      const isTileRoof =
        roofTypeName === 'Tile Clay' || roofTypeName === 'Tile Concrete' || roofTypeName === 'Tile Slate'
      if (isTileRoof) {
        return { ...baseFastener, name: K2_UK_SINGLE_RAIL_TILE_WOOD_SCREW_PACK_CODE }
      }
    } else if (railType === 'solidRail') {
      // SolidRail uses the Spax wood screw pack SKU
      return { ...baseFastener, name: K2_UK_SPAX_WOOD_SCREW_PACK_CODE }
    }
    return baseFastener
  }
}

class K2OnRoofUKBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    // Check thermal separation warning based on total module array length (applies to all rail types)
    this.checkThermalSeparationWarning()

    const railType = this.input.options.k2UKRailType as
      | 'minirail'
      | 'singleRail'
      | 'solidRail'
      | 'multiRail10'
      | 'multiRail25'
      | 'multiRail10CSM'
      | 'multiRail25CSM'
      | undefined

    if (
      railType === 'multiRail10' ||
      railType === 'multiRail25' ||
      railType === 'multiRail10CSM' ||
      railType === 'multiRail25CSM'
    ) {
      //Only multirail is not compatible with portrait orientation so we can't move this to system level check
      const system = window.editor.selectedSystem
      if (this.block.orientation !== 'landscape') {
        logAmplitudeEvent('integrated_racking_dialog_error', {
          integration: 'K2UK',
          errorType: 'compatibility',
          error_msg: 'MultiRail is only compatible with landscape panel orientation',
          railType: railType,
          orientation: this.block.orientation,
        })
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message:
            'MultiRail is only compatible with landscape panel orientation. Please change your panel layout to landscape.',
          key: `K2UK_MULTIRAIL_ORIENTATION_WARNING`,
          severity: 'warning',
          systemId: system?.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return result
      } else {
        window.WorkspaceHelper.removeProjectErrorFromReduxStore(
          `K2UK_MULTIRAIL_ORIENTATION_WARNING`,
          system?.uuid,
          'plugin'
        )
      }
      const starterBom = await this.generateMultiRailStarterBom(result, context)
      const finalBom = this.postProcess(starterBom)
      this.trackBomGenerationSuccess(finalBom, railType)
      return finalBom
    }

    if (railType === 'singleRail') {
      const starterBom = await this.generateSingleRailStarterBom(result, context)
      const finalBom = this.postProcess(starterBom)
      this.trackBomGenerationSuccess(finalBom, railType)
      return finalBom
    }

    // Use custom BOM generation for SolidRail (same logic as SingleRail)
    if (railType === 'solidRail') {
      const starterBom = await this.generateSolidRailStarterBom(result, context)
      const finalBom = this.postProcess(starterBom)
      this.trackBomGenerationSuccess(finalBom, railType)
      return finalBom
    }

    const starterBom = await this.generateStarterBom(result, context)
    const finalBom = this.postProcess(starterBom)
    this.trackBomGenerationSuccess(finalBom, railType)
    return finalBom
  }

  trackBomGenerationSuccess(result: MountingCalcResult, railType: string | undefined): void {
    const system = window.editor.selectedSystem
    const activePanels = this.block.rows.flat().filter((panel) => panel?.isActive)
    logAmplitudeEvent('selected_native_mounting_system', {
      integration: 'K2UK',
      railType: railType || 'minirail',
      orientation: this.block.orientation,
      roofType: this.input.roof.roofTypeName,
      itemCount: result.items.length,
      systemId: system?.uuid,
      panelCount: activePanels.length,
    })
  }

  checkThermalSeparationWarning(): void {
    const system = window.editor.selectedSystem
    const MAX_ARRAY_LENGTH_MM = 17600 // 17.6 metres in mm

    let totalArrayLength = 0

    // Collect all active panels to find the total array dimensions
    const activePanels: Array<{ left: number; top: number; width: number; height: number }> = []
    this.block.rows.forEach((row) => {
      row.forEach((panel) => {
        if (panel?.isActive) {
          activePanels.push({
            left: panel.left,
            top: panel.top,
            width: panel.width,
            height: panel.height,
          })
        }
      })
    })

    if (activePanels.length === 0) return

    if (this.block.orientation === 'portrait') {
      // For portrait, check total array width (leftmost to rightmost panel)
      const leftmostPanel = activePanels.reduce((min, panel) => (panel.left < min.left ? panel : min))
      const rightmostPanel = activePanels.reduce((max, panel) =>
        panel.left + panel.width > max.left + max.width ? panel : max
      )
      totalArrayLength = rightmostPanel.left + rightmostPanel.width - leftmostPanel.left
    } else {
      // For landscape, check total array height (topmost to bottommost panel)
      const topmostPanel = activePanels.reduce((min, panel) => (panel.top < min.top ? panel : min))
      const bottommostPanel = activePanels.reduce((max, panel) =>
        panel.top + panel.height > max.top + max.height ? panel : max
      )
      totalArrayLength = bottommostPanel.top + bottommostPanel.height - topmostPanel.top
    }

    if (totalArrayLength > MAX_ARRAY_LENGTH_MM) {
      logAmplitudeEvent('integrated_racking_dialog_error', {
        integration: 'K2UK',
        errorType: 'thermal_separation',
        error_msg: 'Array is larger than recommended for thermal spacing',
        totalArrayLength: totalArrayLength,
        maxArrayLength: MAX_ARRAY_LENGTH_MM,
        orientation: this.block.orientation,
        railType: String(this.input.options.k2UKRailType || 'unknown'),
      })
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message:
          'Your array is larger than recommended. Please split your array to allow for thermal spacing (maximum array length: 17.6m).',
        key: `K2UK_THERMAL_SEPARATION_WARNING`,
        severity: 'warning',
        systemId: system?.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
    } else {
      window.WorkspaceHelper.removeProjectErrorFromReduxStore(`K2UK_THERMAL_SEPARATION_WARNING`, system?.uuid, 'plugin')
    }
  }

  generateStarterBom(result: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    if (this.block.orientation === 'portrait') {
      return this.generateBomFrom(K2UKPortraitBomGenerator, result, context)
    } else {
      return this.generateBomFrom(K2UKLandscapeBomGenerator, result, context)
    }
  }

  async generateSingleRailStarterBom(
    result: MountingCalcResult,
    context: BomGeneratorContext
  ): Promise<MountingCalcResult> {
    const bomResult =
      this.block.orientation === 'portrait'
        ? await this.generateBomFrom(K2UKSingleRailPortraitBomGenerator, result, context)
        : await this.generateBomFrom(K2UKSingleRailLandscapeBomGenerator, result, context)

    return bomResult
  }

  async generateMultiRailStarterBom(
    result: MountingCalcResult,
    context: BomGeneratorContext
  ): Promise<MountingCalcResult> {
    return await this.generateBomFrom(K2UKMultiRailLandscapeBomGenerator, result, context)
  }

  async generateSolidRailStarterBom(
    result: MountingCalcResult,
    context: BomGeneratorContext
  ): Promise<MountingCalcResult> {
    const bomResult =
      this.block.orientation === 'portrait'
        ? await this.generateBomFrom(K2UKSolidRailPortraitBomGenerator, result, context)
        : await this.generateBomFrom(K2UKSolidRailLandscapeBomGenerator, result, context)

    return bomResult
  }

  postProcess(result: MountingCalcResult): MountingCalcResult {
    const railType = this.input.options.k2UKRailType as
      | 'minirail'
      | 'singleRail'
      | 'solidRail'
      | 'multiRail10'
      | 'multiRail25'
      | 'multiRail10CSM'
      | 'multiRail25CSM'
      | undefined

    let resultWithRoofHooks = result
    const isMultiRail =
      railType === 'multiRail10' ||
      railType === 'multiRail25' ||
      railType === 'multiRail10CSM' ||
      railType === 'multiRail25CSM'
    const roofTypeName = this.input.roof.roofTypeName
    // Add roof hooks for singleRail and solidRail only on Tile Slate (not MultiRail)
    const shouldAddRoofHooks = railType === 'singleRail' || (railType === 'solidRail' && roofTypeName === 'Tile Slate')

    if (shouldAddRoofHooks) {
      resultWithRoofHooks = new K2UKRoofHookAdder(this.input, this.block).process(result)
    }

    const rows = this.block.rows.length
    const cols = this.block.rows[0]?.length || 0
    const cableManagerQuantity = rows * cols

    if (cableManagerQuantity > 0) {
      const cableManagers: Item[] = Array(cableManagerQuantity)
        .fill(null)
        .map(() => chooseK2UKCableManager(this.input))
      result = addItems(resultWithRoofHooks, cableManagers)
    } else {
      result = resultWithRoofHooks
    }

    if (isMultiRail) {
      const roofTypeName = this.input.roof.roofTypeName
      const multiRailComponentCode =
        railType === 'multiRail10CSM' || railType === 'multiRail25CSM' || roofTypeName === 'Metal Tin'
          ? '2004209' //TODO: Add 2004209 on component database
          : '2002793'

      result = {
        ...result,
        items: result.items.filter(
          (item) =>
            !item.components.some(
              (component) => component.type === MountingComponentType.RAIL && component.name === multiRailComponentCode
            )
        ),
      }

      const midClampCount = result.items.filter((item) => item.name === '2004540').length
      const endClampCount = result.items.filter((item) => item.name === '2004545').length
      const multiRailQuantity = midClampCount + endClampCount

      if (multiRailQuantity > 0) {
        const multiRailItems: Item[] = Array(multiRailQuantity)
          .fill(null)
          .map(() => ({
            name: multiRailComponentCode,
            components: [],
          }))
        result = addItems(result, multiRailItems)

        const screwsPerClamp = railType === 'multiRail10' || railType === 'multiRail10CSM' ? 2 : 4
        const totalScrews = multiRailQuantity * screwsPerClamp
        if (totalScrews > 0) {
          const metalScrews: Item[] = Array(totalScrews)
            .fill(null)
            .map(() => chooseK2UKMetalScrew(this.input))
          result = addItems(result, metalScrews)
        }
      }
    }

    if (railType === 'singleRail' || railType === 'solidRail') {
      result = new K2UKRailSplicer(this.input, this.block).process(result)

      const railRows = this.block.orientation === 'portrait' ? rows * 2 : cols * 2
      const blackCoverQuantity = railRows * 2

      if (blackCoverQuantity > 0) {
        const blackCovers: Item[] = Array(blackCoverQuantity)
          .fill(null)
          .map(() => chooseK2UKBlackCover(this.input))
        result = addItems(result, blackCovers)
      }

      // Add flashings for Tile Slate (solidRail only) and Tile Clay (singleRail only)
      const roofTypeName = this.input.roof.roofTypeName
      if (
        (railType === 'solidRail' && roofTypeName === 'Tile Slate') ||
        (railType === 'singleRail' && roofTypeName === 'Tile Clay')
      ) {
        const flashingQuantity = railRows * 2
        if (flashingQuantity > 0) {
          const flashings: Item[] = Array(flashingQuantity)
            .fill(null)
            .map(() => chooseK2UKFlashing(this.input))
          result = addItems(result, flashings)
        }
      }
    }

    // Collapse pack-sized wood screw SKUs so each `Item` row matches one purchasable unit (`addItemsToSystem`
    // counts rows as qty 1). `result.fasteners` is still filled in `RoofHookAdderAbstract` with physical screw
    // counts per SKU and drives MCS `fixing_qty` via `saveRoofFacetData` — unchanged here. Any UX that shows
    // both fixing_qty and BOM SKU quantities may intentionally differ (pieces vs pack lines); confirm with
    // product if that pairing is exposed and could confuse users.
    return {
      ...result,
      items: collapseK2UKSpaxWoodScrewPackItems(collapseK2UKSingleRailTileWoodScrewPackItems(result.items)),
    }
  }
}

class K2UKPortraitBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'horizontal')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    // Endcaps not used for Minirail
    return null
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const railOverhang = 50
    const distanceFromEdge = panel.height * 0.1 // 10% from top/bottom edges

    return [
      {
        top: distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.height - distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

class K2UKSingleRailPortraitBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'horizontal')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    return chooseK2UKEndCap(this.input)
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const railOverhang = 50
    const distanceFromEdge = panel.height * 0.1 // 10% from top/bottom edges

    return [
      {
        top: distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.height - distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

class K2UKLandscapeBomGenerator extends PerColumnRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'vertical')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    // Endcaps not used for Minirail
    return null
  }

  getRailPositions(panel: Panel): VerticalRailPosition[] {
    const railOverhang = 50
    const distanceFromEdge = panel.width * 0.1

    return [
      {
        left: distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
      {
        left: panel.width - distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
    ]
  }
}

class K2UKMultiRailLandscapeBomGenerator extends PerColumnRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'vertical')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    return null
  }

  getRailPositions(panel: Panel): VerticalRailPosition[] {
    const railOverhang = 50
    const distanceFromEdge = panel.width * 0.1 // 10% from left/right edges

    return [
      {
        left: distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
      {
        left: panel.width - distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
    ]
  }
}

class K2UKSingleRailLandscapeBomGenerator extends PerColumnRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'vertical')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    return chooseK2UKEndCap(this.input)
  }

  getRailPositions(panel: Panel): VerticalRailPosition[] {
    const railOverhang = 5
    const distanceFromEdge = panel.width * 0.1

    return [
      {
        left: distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
      {
        left: panel.width - distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
    ]
  }
}
class K2UKSolidRailPortraitBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'horizontal')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    return chooseK2UKEndCap(this.input)
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const railOverhang = 50
    const distanceFromEdge = panel.height * 0.1 // 10% from top/bottom edges

    return [
      {
        top: distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.height - distanceFromEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

class K2UKSolidRailLandscapeBomGenerator extends PerColumnRailBomGeneratorAbstract {
  chooseRail(): RailComponent {
    return chooseK2UKRail(this.input, 'vertical')
  }

  chooseMidClamp(): Item {
    return chooseK2UKMidClamp(this.input)
  }

  chooseEndClamp(): Item {
    return chooseK2UKEndClamp(this.input)
  }

  chooseEndCap(): Item | null {
    return chooseK2UKEndCap(this.input)
  }

  getRailPositions(panel: Panel): VerticalRailPosition[] {
    const railOverhang = 5
    const distanceFromEdge = panel.width * 0.1

    return [
      {
        left: distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
      {
        left: panel.width - distanceFromEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
    ]
  }
}

function chooseK2UKRail(input: MountingCalcInput, direction: Direction): RailComponent {
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined

  // Map K2UK rail types to RailType
  // minirail -> 'mini' rail type
  // All other types -> 'continuous' rail type
  const railTypeMapping: RailType = railType === 'minirail' ? 'mini' : 'continuous'

  let componentCode = '2004258' // Default to singleRail
  const roofTypeName = input.roof.roofTypeName

  if (railType === 'minirail') {
    componentCode = '2004211'
  } else if (railType === 'singleRail') {
    componentCode = '2004258'
  } else if (railType === 'solidRail') {
    componentCode = '2004264' // SolidRail
  } else if (railType === 'multiRail10' || railType === 'multiRail25') {
    // MultiRail for Trapezoidal: 2002793
    // MultiRail for Metal Tin: 2004209
    componentCode = roofTypeName === 'Metal Tin' ? '2004209' : '2002793'
  } else if (railType === 'multiRail10CSM' || railType === 'multiRail25CSM') {
    // CSM variants are for Metal Tin (corrugated)
    componentCode = '2004209'
  }

  return {
    type: MountingComponentType.RAIL,
    railType: railTypeMapping,
    direction,
    name: componentCode,
    length: 0,
    top: 0,
    left: 0,
  }
}

function chooseK2UKMidClamp(input: MountingCalcInput): Item {
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined
  // Use 2004537 for solidRail, otherwise use default 2004540
  const componentCode = railType === 'solidRail' ? '2004537' : '2004540'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKEndClamp(input: MountingCalcInput): Item {
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined
  // Use 2004543 for solidRail, otherwise use default 2004545
  // TODO: Add 2004543 on component database
  const componentCode = railType === 'solidRail' ? '2004543' : '2004545'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKEndCap(input: MountingCalcInput): Item | null {
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined
  // TODO: Add 2004796 on component database
  const componentCode = railType === 'solidRail' ? '2004796' : '1004767'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKCableManager(input: MountingCalcInput): Item {
  const componentCode = '2002870'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKMetalScrew(input: MountingCalcInput): Item {
  const componentCode = '1005207' // Metal screws for MultiRail
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKFlashing(input: MountingCalcInput): Item {
  // Use 2004282 for Tile Clay with singleRail, 2004569 for Tile Slate with solidRail
  const roofTypeName = input.roof.roofTypeName
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined
  // TODO: Add 2004282 on component database
  const componentCode = roofTypeName === 'Tile Clay' && railType === 'singleRail' ? '2004282' : '2004569'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKBlackCover(input: MountingCalcInput): Item {
  const railType = input.options.k2UKRailType as
    | 'minirail'
    | 'singleRail'
    | 'solidRail'
    | 'multiRail10'
    | 'multiRail25'
    | 'multiRail10CSM'
    | 'multiRail25CSM'
    | undefined
  // Use 2004797 for solidRail, otherwise use default 2003523
  const componentCode = railType === 'solidRail' ? '2004797' : '2003523'
  return {
    name: componentCode,
    components: [],
  }
}

function chooseK2UKMiniRailSet(input: MountingCalcInput): Item {
  const componentCode = 'K2-UK-MINIRAIL-SET-PLACEHOLDER'
  return {
    name: componentCode,
    components: [],
  }
}

class K2UKRailSplicer extends CutAndSplicerAbstract {
  isTarget(component: Component): boolean {
    return component?.type === MountingComponentType.RAIL
  }

  chooseSplice(railName: string, input: MountingCalcInput): Item[] {
    // Use 1004107 for solidRail, otherwise use default
    const componentCode = railName === '2004264' ? '1004107' : '2001976'
    return [
      {
        name: componentCode,
        components: [
          {
            type: MountingComponentType.SPLICE,
            name: componentCode,
            top: 0,
            left: 0,
          },
        ],
      },
    ]
  }

  getFullLength(railName: string): number {
    if (railName === '2004258') return 3650 // SingleRail
    if (railName === '2004264') return 3650 // SolidRail
    return 0
  }
}
